import React, { useState } from 'react';
import { Button, InputGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContentModal } from '../UnifiedModal';
import MarketplaceBadge from '../MarketplaceBadge';
import SocialShareButtons from '../SocialShareButtons';
import {
  faShareAlt,
  faGlobe,
  faLock,
  faCopy,
  faCheck,
  faUsers,
  faStar,
  faEye,
  faEyeSlash,
  faKey,
  faArrowRight,
  faShieldAlt,
  faCode
} from '@fortawesome/free-solid-svg-icons';
import './ShareStrategyModal.css';

const ShareStrategyModal = ({
  show,
  onHide,
  strategyName,
  isPublic,
  shareUrl,
  onMakePublic,
  onMakePrivate,
  isInMarketplace = false,
  marketplaceListingId = null,
  isOwner = true,
  existingPrivateShare = null,
  shareInfoLoading = false,
  onRefreshShareInfo,
  listingFlow = false,
  onListingPublished = (_listingId) => {}
}) => {
  const [copied, setCopied] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [shareType, setShareType] = useState(listingFlow ? 'public' : null);
  const [privateShareCreated, setPrivateShareCreated] = useState(false);
  const [privateShareUrl, setPrivateShareUrl] = useState('');
  const [forceRegenerate, setForceRegenerate] = useState(false);
  // Track when we just published to marketplace (before prop updates)
  const [justPublished, setJustPublished] = useState(false);
  const [justPublishedListingId, setJustPublishedListingId] = useState(null);
  // Track when strategy was just made private (unpublished)
  const [justMadePrivate, setJustMadePrivate] = useState(false);
  // Protect code toggle - when true, users can run/deploy but cannot view/copy code
  const [protectCode, setProtectCode] = useState(listingFlow);

  // Reset state when modal opens or closes
  React.useEffect(() => {
    if (!show) {
      // Reset everything when modal closes
      setCopied(false);
      setShareType(listingFlow ? 'public' : null);
      setIsToggling(false); // Reset loading state
      setPrivateShareCreated(false);
      setPrivateShareUrl('');
      setForceRegenerate(false);
      setJustPublished(false);
      setJustPublishedListingId(null);
      setJustMadePrivate(false);
      setProtectCode(listingFlow);
    } else {
      // When modal opens, set default based on context
      if (isInMarketplace || listingFlow) {
        setShareType('public');
      } else {
        setShareType(null);
      }
      setIsToggling(false);
      setForceRegenerate(false);
      setProtectCode(listingFlow);
    }
  }, [show, isInMarketplace, listingFlow]);

  const copyUrlToClipboard = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleCopyUrl = () => {
    const urlToCopy = privateShareCreated
      ? privateShareUrl
      : (shareType === 'private' && existingPrivateShare?.shareUrl)
        ? existingPrivateShare.shareUrl
        : shareUrl;
    copyUrlToClipboard(urlToCopy);
  };

  const handleToggleVisibility = async (shouldForceRegenerate = false) => {
    setIsToggling(true);
    try {
      // Zoom-style: No password required - backend auto-generates secure token
      // For public shares, pass allowClone based on protectCode toggle
      const options = {
        forceRegenerate: shouldForceRegenerate || forceRegenerate,
        allowClone: listingFlow ? false : !protectCode
      };
      const result = await onMakePublic(undefined, shareType, options);

      if (result === false) {
        // Error already handled by parent component with toast
        setTimeout(() => onHide(), 500);
        return;
      }

      // Check if this was a private share creation
      if (result?.isPrivate && result?.success && result?.shareUrl) {
        // Private share created - show success state with auto-generated link
        setPrivateShareCreated(true);
        setPrivateShareUrl(result.shareUrl);
        setIsToggling(false);
        if (onRefreshShareInfo) {
          onRefreshShareInfo();
        }
      } else if (shareType === 'private' && result?.shareUrl) {
        // Alternative check for private shares
        setPrivateShareCreated(true);
        setPrivateShareUrl(result.shareUrl);
        setIsToggling(false);
      } else if (result?.success && result?.isPublic) {
        const listingId = result.marketplaceListingId || marketplaceListingId;
        if (listingFlow && listingId) {
          setIsToggling(false);
          onListingPublished(listingId);
          return;
        }

        // Public share success - set justPublished to show success state
        console.log('[ShareStrategyModal] Public share success:', result);
        setJustPublished(true);
        setJustPublishedListingId(listingId || null);
        setIsToggling(false);
        if (onRefreshShareInfo) {
          onRefreshShareInfo();
        }
      } else {
        // Fallback - reset loading state
        setIsToggling(false);
      }
    } catch (error) {
      // Error is handled by parent component with toast
      console.error('[ShareStrategyModal] Error:', error);
      setIsToggling(false);
      // Don't throw - parent already handles errors
      // Just close modal to show toast
      setTimeout(() => onHide(), 500);
      return false;
    }
  };

  const handleMakePrivate = async () => {
    // CRITICAL: onMakePrivate MUST be provided - no fallback allowed
    // Previous fallback to onMakePublic caused silent bugs where clicking
    // "Make Private" would publish instead of unpublish
    if (!onMakePrivate) {
      console.error('[ShareStrategyModal] CRITICAL: onMakePrivate prop is required but not provided!');
      alert('Unable to make strategy private - configuration error. Please report this bug.');
      return false;
    }

    setIsToggling(true);
    try {
      const result = await onMakePrivate();
      if (result === false) {
        // Error already handled by parent component with toast
        setIsToggling(false);
        return;
      }
      // Success - show success state instead of closing modal
      setIsToggling(false);
      setJustMadePrivate(true);
    } catch (error) {
      // Error is handled by parent component with toast
      console.error('[ShareStrategyModal] Error during makePrivate:', error);
      setIsToggling(false);
      return false;
    }
  };

  return (
    <ContentModal 
      isOpen={show} 
      onClose={onHide} 
      size="md" 
      className="share-strategy-modal"
      title={listingFlow ? 'List Strategy' : 'Share Strategy'}
      ariaLabel={listingFlow ? 'List Strategy' : 'Share Strategy'}
    >
        {!isOwner ? (
          <>
            {/* Non-owner view - show marketplace link + social share (no publish/unpublish) */}
            <div className="share-link-section">
              <div className="strategy-header">
                <div className="strategy-name">{strategyName}</div>
                <div className="strategy-subtitle">Share this strategy with others</div>
              </div>

              <div className="share-link-container">
                <label className="link-label">Marketplace link</label>
                <div className="link-input-group">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="link-input"
                  />
                  <button
                    className={`copy-btn ${copied ? 'copied' : ''}`}
                    onClick={handleCopyUrl}
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                    <span>{copied ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {/* Social share buttons for non-owners */}
              {shareUrl && (
                <SocialShareButtons
                  url={shareUrl}
                  text={`Check out "${strategyName}" on BotSpot Marketplace!`}
                  className="compact"
                />
              )}

              <div className="share-info">
                <FontAwesomeIcon icon={faGlobe} className="me-2" />
                <span className="text-muted">This strategy is available in the marketplace</span>
              </div>
            </div>
          </>
        ) : justMadePrivate ? (
          <>
            {/* Success State - Just Made Private - Clean text only */}
            <div className="private-removed-state">
              <h5 className="private-removed-title">Removed from Marketplace</h5>
              <p className="private-removed-desc">
                <strong>"{strategyName}"</strong> is now private and no longer visible to other users.
              </p>
            </div>

            {/* Action button */}
            <div className="modal-action-buttons">
              <Button variant="primary" onClick={onHide} size="sm">
                Done
              </Button>
            </div>
          </>
        ) : (isInMarketplace || justPublished || privateShareCreated) ? (
          <>
            {/* Success State */}
            <div className="success-state">
              {/* Marketplace success */}
              {(isInMarketplace || justPublished) && !privateShareCreated && (
                <>
                  <div className="success-hero-badge">
                    <MarketplaceBadge size="hero" />
                  </div>
                  <h5 className="success-title">Your strategy is live!</h5>
                  <p className="success-desc">
                    "{strategyName}" is now publicly available in the marketplace
                  </p>
                  {(marketplaceListingId || justPublishedListingId) && (
                    <a
                      href={`/marketplace/strategy/${marketplaceListingId || justPublishedListingId}`}
                      className="marketplace-view-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View your listing
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    </a>
                  )}
                </>
              )}
              {/* Private share success - simple and clean */}
              {privateShareCreated && (
                <>
                  <div className="success-icon">
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                  <h5 className="success-title">Link Created</h5>
                  <p className="success-desc">
                    Share this link with anyone you want to give access to "{strategyName}"
                  </p>
                </>
              )}
            </div>

            {/* Share Link - Only for private shares */}
            {privateShareCreated && (
              <div className="private-share-success">
                <div className="private-link-box">
                  <input
                    type="text"
                    value={privateShareUrl}
                    readOnly
                    className="private-link-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    className={`copy-btn-inline ${copied ? 'copied' : ''}`}
                    onClick={handleCopyUrl}
                  >
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <SocialShareButtons
                  url={privateShareUrl}
                  text={`Check out "${strategyName}" on BotSpot!`}
                  className="compact"
                />
                <div className="modal-action-buttons">
                  <Button variant="secondary" onClick={onHide} size="sm">
                    Done
                  </Button>
                </div>
              </div>
            )}

            {/* Social Share & Actions for marketplace */}
            {!privateShareCreated && (
              <div className="marketplace-footer">
                <SocialShareButtons
                  url={`${window.location.origin}/marketplace/strategy/${marketplaceListingId || justPublishedListingId}`}
                  text={`Check out my trading strategy "${strategyName}" on BotSpot!`}
                />
                <div className="modal-action-buttons">
                  <Button
                    variant="outline-danger"
                    onClick={handleMakePrivate}
                    disabled={isToggling}
                    size="sm"
                  >
                    {isToggling ? 'Removing...' : 'Make Private'}
                  </Button>
                  <Button variant="secondary" onClick={onHide} size="sm">
                    Close
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Strategy Name Header - Elegant */}
            <div className="strategy-header">
              <div className="strategy-name">{strategyName}</div>
              <div className="strategy-subtitle">
                {listingFlow ? 'Publish this strategy to the marketplace' : 'Choose how to share'}
              </div>
            </div>

            {!listingFlow && (
              <div className="share-type-pills">
                <button
                  className={`share-pill ${shareType === 'public' ? 'active' : ''}`}
                  onClick={() => setShareType('public')}
                >
                  <FontAwesomeIcon icon={faGlobe} className="pill-icon" />
                  <span className="pill-label">Marketplace</span>
                </button>
                <button
                  className={`share-pill ${shareType === 'private' ? 'active' : ''}`}
                  onClick={() => setShareType('private')}
                >
                  <FontAwesomeIcon icon={faLock} className="pill-icon" />
                  <span className="pill-label">Private Link</span>
                </button>
              </div>
            )}

            {shareType === 'private' && (
              <div className="share-private-context">
                {shareInfoLoading ? (
                  <div className="share-loading">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    <span className="ms-2">Loading...</span>
                  </div>
                ) : existingPrivateShare?.shareUrl ? (
                  <>
                    <div className="existing-link-section">
                      <div className="existing-link-header">
                        <span className="existing-link-label">Current private link</span>
                        <button
                          className={`copy-icon-btn ${copied ? 'copied' : ''}`}
                          onClick={() => copyUrlToClipboard(existingPrivateShare.shareUrl)}
                          title={copied ? 'Copied!' : 'Copy link'}
                        >
                          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={existingPrivateShare.shareUrl}
                        readOnly
                        className="private-link-input"
                        onClick={(e) => e.target.select()}
                      />
                    </div>
                    <SocialShareButtons
                      url={existingPrivateShare.shareUrl}
                      text={`Check out "${strategyName}" on BotSpot!`}
                      className="compact"
                    />
                  </>
                ) : null}
              </div>
            )}

            {/* Explanation - Only show after selecting share type */}
            {shareType && (
              <div className="share-explanation">
                {shareType === 'private' ? (
                  <p>
                    Generate a secure private link that only people you share it with can access.
                    Your strategy will <strong>not</strong> appear in the marketplace.
                  </p>
                ) : (
                  <p>
                    {protectCode
                      ? 'Publish your strategy to the BotSpot Marketplace so others can discover and deploy it without exposing your source code.'
                      : 'Publish your strategy to the BotSpot Marketplace where others can discover, view, and clone it.'}
                  </p>
                )}
              </div>
            )}

            {/* Code Protection Toggle - Only for regular public shares. Listings are protected by default. */}
            {shareType === 'public' && !listingFlow && (
              <div className="protect-code-section">
                <div className="protect-code-toggle">
                  <Form.Check
                    type="switch"
                    id="protect-code-toggle"
                    checked={protectCode}
                    onChange={(e) => setProtectCode(e.target.checked)}
                    label={
                      <span className="protect-label">
                        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                        Protect my code
                      </span>
                    }
                  />
                </div>
                <div className={`protect-code-info ${protectCode ? 'protected' : ''}`}>
                  {protectCode ? (
                    <>
                      <FontAwesomeIcon icon={faLock} className="info-icon protected" />
                      <span>Users can <strong>run and deploy</strong> your strategy, but <strong>cannot view or copy</strong> the code.</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCode} className="info-icon" />
                      <span>Users can view, copy, and modify your code freely.</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="modal-action-buttons">
              <Button
                variant="primary"
                onClick={() => {
                  // Pass true directly if regenerating (don't rely on async state)
                  const shouldRegenerate = shareType === 'private' && !!existingPrivateShare?.shareUrl;
                  handleToggleVisibility(shouldRegenerate);
                }}
                disabled={
                  isToggling ||
                  !shareType ||
                  (isInMarketplace && isPublic && shareType === 'public')
                }
                size="sm"
              >
                {isToggling ? 'Working...' : !shareType ? 'Choose an option above' :
                  shareType === 'private' ?
                    (existingPrivateShare?.shareUrl ? 'Regenerate Link' : 'Generate Link') :
                    (isInMarketplace && isPublic ? 'Already Published' : 'Publish to Marketplace')}
              </Button>
              <Button variant="secondary" onClick={onHide} size="sm">
                Cancel
              </Button>
            </div>
          </>
        )}
    </ContentModal>
  );
};

export default ShareStrategyModal; 
